import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  makeStyles,
  IconButton,
  Button,
  Snackbar,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from '@material-ui/core';
import {
  Storage as StorageIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  Warning as WarningIcon,
} from '@material-ui/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { JiraTicket } from '../../types';
import { DarkTooltip } from '../AgentChatComponent/DarkTooltip';
import { MarkdownEditor } from '../AgentChatComponent/MarkdownEditor';
import { FEATURES } from '../../features';

interface STSPlanPreviewProps {
  planMarkdown: string | null;
  selectedTicket: JiraTicket | null;
  mobileMode?: boolean;
  tabletMode?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  onPlanUpdated?: (newPlan: string) => void;
  sendMessage?: (message: string) => void;
}

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  header: {
    flexShrink: 0,
    padding: theme.spacing(2, 2.5, 1.5),
    borderBottom: '1px solid #e0e0e0',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: '#000000',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  subtitle: {
    fontSize: '0.75rem',
    color: '#616161',
    marginTop: theme.spacing(0.25),
  },
  editActions: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  btnSave: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '4px 14px',
    borderRadius: 8,
    backgroundColor: '#1976d2',
    color: '#fff',
    '&:hover': { backgroundColor: '#1565c0' },
    '&:disabled': { backgroundColor: '#90caf9', color: '#fff' },
  },
  btnDiscard: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '4px 14px',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    color: '#374151',
    backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#f9fafb' },
  },
  content: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(2.5),
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[300], borderRadius: 2 },
  },
  markdownBox: {
    fontSize: '0.875rem',
    lineHeight: 1.7,
    color: theme.palette.text.primary,
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    '& h1, & h2, & h3, & h4': { fontWeight: 700, margin: '12px 0 6px' },
    '& p': { margin: '4px 0' },
    '& ul, & ol': { paddingLeft: 20, margin: '4px 0' },
    '& li': { marginBottom: 2 },
    '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, margin: '10px 0' },
    '& strong': { fontWeight: 700 },
    '& table': { borderCollapse: 'collapse', width: '100%', margin: '8px 0', fontSize: '0.82rem' },
    '& th, & td': { border: `1px solid ${theme.palette.grey[300]}`, padding: theme.spacing(0.75, 1.5), textAlign: 'left' },
    '& th': { backgroundColor: theme.palette.grey[100], fontWeight: 700 },
    '& tr:nth-child(even)': { backgroundColor: theme.palette.grey[50] },
    '& a': { color: '#1976d2', textDecoration: 'underline' },
    '& code': { backgroundColor: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontSize: '0.82rem', fontFamily: 'monospace' },
    '& pre': { backgroundColor: '#f1f5f9', padding: theme.spacing(1.5), borderRadius: 6, overflowX: 'auto' },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  validationError: {
    fontSize: '0.72rem',
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    padding: '4px 10px',
    margin: theme.spacing(0.5, 1.5, 0),
    flexShrink: 0,
  },
  dirtyDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#f59e0b',
    display: 'inline-block',
    marginLeft: 6,
    flexShrink: 0,
  },
}));

function validateMarkdown(value: string): string | null {
  if (!value.trim()) return 'Plan cannot be empty.';
  const fenceMatches = (value.match(/^```/gm) || []).length;
  if (fenceMatches % 2 !== 0) return 'Unclosed code block detected (``` without closing).';
  return null;
}

export const STSPlanPreview: React.FC<STSPlanPreviewProps> = ({
  planMarkdown,
  selectedTicket,
  onDirtyChange,
  onPlanUpdated,
  sendMessage,
}) => {
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; error?: boolean }>({ open: false, message: '' });
  const [discardDialog, setDiscardDialog] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setIsEditing(false);
      setIsDirty(false);
      setValidationError(null);
      onDirtyChange?.(false);
    }
  }, [selectedTicket?.id]);

  const handleEditStart = () => {
    setEditValue(planMarkdown || '');
    setIsDirty(false);
    setValidationError(null);
    setIsEditing(true);
  };

  const handleEditorChange = useCallback((value: string) => {
    setEditValue(value);
    const dirty = value !== (planMarkdown || '');
    setIsDirty(dirty);
    onDirtyChange?.(dirty);
    if (validationError) setValidationError(validateMarkdown(value));
  }, [planMarkdown, validationError, onDirtyChange]);

  const exitEdit = () => {
    setIsEditing(false);
    setIsDirty(false);
    setValidationError(null);
    onDirtyChange?.(false);
  };

  const handleDiscard = () => {
    if (isDirty) setDiscardDialog(true);
    else exitEdit();
  };

  const handleSave = async () => {
    const error = validateMarkdown(editValue);
    if (error) { setValidationError(error); return; }

    setIsSaving(true);
    try {
      const backendUrl = configApi.getString('backend.baseUrl');
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/custom-api/requirement-agent/plans/${selectedTicket!.ticketNumber}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ plan: editValue }),
        },
      );
      if (!res.ok) throw new Error(`Save failed: ${res.status}`);
      onPlanUpdated?.(editValue);
      exitEdit();
      setSnackbar({ open: true, message: 'Plan saved successfully.' });
      sendMessage?.(`Plan for ticket ${selectedTicket!.ticketNumber} has been updated.`);
    } catch {
      setSnackbar({ open: true, message: 'Failed to save plan. Please try again.', error: true });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerRow}>
          <Typography className={classes.sectionTitle}>
            <StorageIcon style={{ fontSize: 18 }} />
            Requirements Plan
            {isEditing && isDirty && <span className={classes.dirtyDot} />}
          </Typography>

          {selectedTicket && planMarkdown && !isEditing && FEATURES.PLAN_EDIT_ENABLED && (
            <DarkTooltip title="Edit plan" placement="top" arrow>
              <IconButton size="small" onClick={handleEditStart}>
                <EditIcon style={{ fontSize: 16 }} />
              </IconButton>
            </DarkTooltip>
          )}

          {isEditing && FEATURES.PLAN_EDIT_ENABLED && (
            <Box className={classes.editActions}>
              <Button
                className={classes.btnDiscard}
                startIcon={<CloseIcon style={{ fontSize: 14 }} />}
                onClick={handleDiscard}
                disabled={isSaving}
              >
                Discard
              </Button>
              <Button
                className={classes.btnSave}
                startIcon={isSaving ? <CircularProgress size={12} style={{ color: '#fff' }} /> : <SaveIcon style={{ fontSize: 14 }} />}
                onClick={handleSave}
                disabled={isSaving || !isDirty}
              >
                {isSaving ? 'Saving…' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </Box>

        {!isEditing && (
          <Typography className={classes.subtitle}>
            Requirements gathered through agent conversation
          </Typography>
        )}
      </Box>

      {isEditing && validationError && (
        <Typography className={classes.validationError}>⚠ {validationError}</Typography>
      )}

      {!isEditing ? (
        <Box className={classes.content}>
          {!selectedTicket ? (
            <Box className={classes.emptyState}>
              <StorageIcon style={{ fontSize: 40, color: 'inherit', marginBottom: 12 }} />
              <Typography variant="body2">Select a ticket to view its plan</Typography>
            </Box>
          ) : !planMarkdown ? (
            <Box className={classes.emptyState}>
              <Typography variant="body2">No plan found.</Typography>
            </Box>
          ) : (
            <Box className={classes.markdownBox}>
              <ReactMarkdown remarkPlugins={[remarkGfm as any]}>{planMarkdown}</ReactMarkdown>
            </Box>
          )}
        </Box>
      ) : (
        <MarkdownEditor value={editValue} onChange={handleEditorChange} />
      )}

      <Dialog open={discardDialog} onClose={() => setDiscardDialog(false)} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 16, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'hidden' } }}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 24px 16px', borderBottom: '1px solid #e0e0e0', backgroundColor: '#fff8e1' }}>
          <Box style={{ width: 36, height: 36, borderRadius: 10, backgroundColor: '#fff3cd', border: '1px solid #ffe082', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <WarningIcon style={{ fontSize: 20, color: '#f59e0b' }} />
          </Box>
          <Typography style={{ fontWeight: 700, fontSize: '1rem', color: '#111827' }}>Discard Changes?</Typography>
        </Box>
        <DialogContent style={{ padding: 24, backgroundColor: '#ffffff' }}>
          <DialogContentText style={{ fontSize: '0.875rem', lineHeight: 1.6, color: '#6b7280' }}>
            You have unsaved changes to this plan. Discarding will revert to the last saved version.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid #e0e0e0', backgroundColor: '#fafafa', display: 'flex', gap: 8 }} disableSpacing>
          <Button onClick={() => setDiscardDialog(false)} style={{ textTransform: 'none', fontSize: '0.82rem', borderRadius: 10, color: '#1976d2', border: '1px solid #90caf9', backgroundColor: '#e3f2fd' }}>
            Keep editing
          </Button>
          <Box flex={1} />
          <Button onClick={() => { setDiscardDialog(false); exitEdit(); }} style={{ textTransform: 'none', fontSize: '0.82rem', borderRadius: 10, color: '#6b7280', border: '1px solid #e0e0e0' }}>
            Discard
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
        ContentProps={{ style: { backgroundColor: snackbar.error ? '#b91c1c' : '#16a34a', fontSize: '0.82rem' } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
